import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { State } from 'src/app/home/reducers';
import { DataService } from '@app-core/services/data/data.service';
import { LiveTelematicsService } from '@app-core/services/live-telematics/live-telematics.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { getSideNavigationConfigState } from '@app-shared/reducers';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, forkJoin, fromEvent, of, Subject, timer } from 'rxjs';
import { catchError, filter, finalize, groupBy, ignoreElements, mergeMap, switchMap, takeUntil, timeoutWith, tap } from 'rxjs/operators';
import { Socket } from 'socket.io-client';
import { LiveViewService } from '@app-live-view/services/live-view/live-view.service';
import { LiveViewMapService } from '@app-live-view/services/live-view-map/live-view-map.service';
import {
  AssetState,
  LiveTelematicsMessage,
  LTMessageType,
  TripAssetDetails,
  AggregateByState,
  LatestTripsAssetListResponse,
  LiveDevice,
  IntitialActiveDevice,
  LatestTripsAssetListRequest,
} from '@app-live-view/models/live-view.model';
import { GoogleTagManagerService, ToggleState } from '@app-core/services/google-tag-manager/google-tag-manager.service';

import { UpdateLiveViewTagsFilter, UpdateLiveViewViewType } from '@app-live-view/actions/live-view.action';
import { getDurationFilterState, getLiveViewFilter, State as liveViewState } from '@app-live-view/reducers';
import { MatDrawer } from '@angular/material/sidenav';
import { BREAKPOINTS_XSMALL, LIVEVIEW_ASSET_LIST_PAGE_SIZE } from '@app-core/constants/constants';
import { MapboxLiveviewComponent } from '@app-live-view/components/mapbox-liveview/mapbox-liveview.component';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { LIVE_FILTER_OPTION } from '@app-live-view/constants/live-view.constants';
import { AssetAutoCompleteResponseObject } from '@app-core/models/core.model';
import { DateService } from '@app-core/services/date/date.service';
import { TripDetailsService } from '@app-trip-details/services/trip-details.service';
import { DurationRange } from '@app-core/models/core.model';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { UpdateDurationFilter } from '@app-live-view/actions/duration-filter.actions';

@Component({
  selector: 'app-live-view',
  templateUrl: './live-view.component.html',
  styleUrls: ['./live-view.component.scss'],
  providers: [LiveViewService, LiveViewMapService],
})
export class LiveViewComponent implements OnInit, OnDestroy {
  @ViewChild('paginator')
  private paginator: MatPaginator;
  @ViewChild(MatDrawer)
  private drawer: MatDrawer;
  @ViewChild('mapboxLiveView')
  private mapboxLiveView: MapboxLiveviewComponent;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private unsubscribeSocketEvents = new Subject<void>();
  private zoomedOutViewSocket: Socket;
  private tagIds = [];
  private ngUnsubscribeOnChanges: Subject<void> = new Subject<void>();
  private unsubscribeLatestTripApi = new Subject<void>();
  private unsubscribeListViewPageEvent = new Subject<void>();

  private skip: number = 0;
  private limit: number = 1000;
  private isSideNavOpen: boolean = false;
  private aggregateByState: AggregateByState = {
    ACTIVE: 0,
    INACTIVE: 0,
    PARKED: 0,
  };
  private listViewTablePageSize: number = 10;
  private clusterViewLimit: number = 1000;
  private assetStateToFilter: string[] = [AssetState.Active, AssetState.Inactive, AssetState.Parked];
  private listViewPageIndex: number = 0;
  private selectedDevice = new BehaviorSubject<LiveDevice>(null);

  public selectedDevice$ = this.selectedDevice.asObservable();
  public isTouchDevice: boolean = false;
  public viewType: string = 'table';
  public clusterAssetsDataSource = new MatTableDataSource<any>([]);
  public displayedColumns: string[] = ['assetId', 'actions'];
  public assetStatusCountdisplayedColumns: string[] = ['status', 'assetCount'];
  public tablePageSize = LIVEVIEW_ASSET_LIST_PAGE_SIZE;
  public searchAssetIdControl = new FormControl({ value: '', disabled: true });
  public mapWidth: string;
  public translateVal: number = 0;
  public currentTheme: string = 'light';
  public assetState = AssetState;
  public mobileTranslateVal: number = 70;
  public isMobileTableView: boolean = false;
  public assetCategories = this.fb.group({
    [AssetState.Active]: true,
    [AssetState.Inactive]: true,
    [AssetState.Parked]: true,
  });
  public optionList = LIVE_FILTER_OPTION;
  public selectedFilter = new FormControl('assetId');
  public zoom: number = 12;
  public liveViewTags: any[];
  public currentView: string = 'list';
  public clusteringAssetDetails: TripAssetDetails[];
  public totalClusterAssetCount: number = 0;
  public clusteringAssetLoader: boolean = false;
  public liveViewLoader: boolean = true;
  public recenterClusterMap: boolean = false;
  public listViewAssetListDataSource = new MatTableDataSource<LiveDevice>([]);
  public durationRange: DurationRange;
  public startDate: string;
  public endDate: string;

  constructor(
    private snackBarService: SnackBarService,
    private liveTelematicsService: LiveTelematicsService,
    private liveViewService: LiveViewService,
    private store: Store<State>,
    private liveViewStore: Store<liveViewState>,
    private fb: FormBuilder,
    private dataService: DataService,
    public translate: TranslateService,
    private gtmService: GoogleTagManagerService,
    private breakpointObserver: BreakpointObserver,
    private LiveViewService: LiveViewService,
    private dateService: DateService,
    private tripDetailsService: TripDetailsService,
    private durationRangeService: DurationRangeService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit() {
    this.store
      .select(getLiveViewFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((liveViewState) => {
        this.currentView = liveViewState.viewType;
        if (this.currentView === 'list') {
          this.limit = this.listViewTablePageSize;
          this.skip = this.listViewPageIndex * this.listViewTablePageSize;
        } else {
          this.limit = this.clusterViewLimit;
          this.skip = 0;
        }
      });

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((durationFilter) => {
        const {
          days,
          displayStartDate: startDate,
          displayEndDate: endDate,
          paramStartDate: paramStartDate,
          paramEndDate: paramEndDate,
        } = durationFilter;
        this.durationRange = { days, startDate, endDate };
        this.startDate = paramStartDate;
        this.endDate = paramEndDate;
      });

    this.breakpointObserver.observe(BREAKPOINTS_XSMALL).subscribe((state: BreakpointState) => {
      this.isTouchDevice = state.matches;
    });

    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
      }
    });
    if (this.durationRangeService.planDetails) {
      const featureFlags = this.durationRangeService.planDetails.featureFlags || [];
      if (!featureFlags.includes('tagging_workflow')) {
        this.optionList = this.optionList.filter((option) => option.id !== 'tags');
      }
    }

    this.subscribeForSideNavChange();
    this.subscribeForAssetCategoriesChange();
    this.subscribeForFleetChange();
  }

  public ngOnDestroy() {
    if (this.zoomedOutViewSocket && this.zoomedOutViewSocket.connected) {
      this.zoomedOutViewSocket.disconnect();
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.unsubscribeSocketEvents.next();
    this.unsubscribeSocketEvents.complete();
    this.ngUnsubscribeOnChanges.next();
    this.ngUnsubscribeOnChanges.complete();
    this.unsubscribeLatestTripApi.next();
    this.unsubscribeLatestTripApi.complete();
    this.unsubscribeListViewPageEvent.next();
    this.unsubscribeListViewPageEvent.complete();
  }

  private moveToFirstPage() {
    this.listViewPageIndex = 0;
    this.paginator?.firstPage();
    // Emitting page event explicitly due to https://github.com/angular/components/issues/8417
    this.paginator?.page.emit({
      length: this.paginator.length,
      pageIndex: 0,
      pageSize: this.tablePageSize,
    });
  }

  private initializePaginator() {
    setTimeout(() => {
      this.unsubscribeListViewPageEvent.next();
      this.paginator.pageIndex = this.listViewPageIndex;
      this.paginator.page.pipe(takeUntil(this.unsubscribeListViewPageEvent)).subscribe((event: PageEvent) => {
        this.listViewPageIndex = event.pageIndex;
        this.skip = event.pageIndex * this.listViewTablePageSize;
        this.gtmService.changeAssetListLiveViewPageChange(event);
        this.listViewAssetListDataSource.data = new Array(10).fill(undefined);
        this.getLatestTripsAssetDetails();
      });
    }, 0);
  }

  private subscribeForSideNavChange() {
    this.store
      .select(getSideNavigationConfigState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sideNavigationConfigState) => {
        const { currentWindowWidth, isSideNavOpen } = sideNavigationConfigState;
        this.isSideNavOpen = isSideNavOpen;
        this.adjustMapWidth(currentWindowWidth);
      });
  }

  private subscribeForAssetCategoriesChange() {
    this.assetCategories.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((categoriesState) => {
      this.assetStateToFilter = Object.keys(categoriesState).filter((key) => categoriesState[key]);
      this.getLatestTripsAssetDetails();
      this.moveToFirstPage();
    });
  }

  private subscribeForFleetChange() {
    this.dataService._currentFleet
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap((fleetId) => {
          if (fleetId) {
            this.searchAssetIdControl.reset();
            this.clusteringAssetDetails = [];
            this.totalClusterAssetCount = 0;
            this.aggregateByState = {
              ACTIVE: 0,
              INACTIVE: 0,
              PARKED: 0,
            };
            this.selectedDevice.next(null);
            this.formatStatusAgrregate();
            this.closeAssetDetails();
            this.listViewAssetListDataSource.data = [];
            if (this.currentView === 'list') {
              this.refreshSocketConnection();
              this.moveToFirstPage();
              this.initializePaginator();
            }
            return this.liveTelematicsService.isLiveTelematicsEnabled;
          }
        }),
        catchError(() => of(false))
      )
      .subscribe((liveTelematicsEnabled) => {
        if (liveTelematicsEnabled) {
          this.getLatestTripsAssetDetails();
        }
      });
  }

  private refreshSocketConnection() {
    this.zoomedOutViewSocket = this.liveTelematicsService.zoomedOutViewSocket;
    this.unsubscribeSocketEvents.next();
    this.unsubscribeLatestTripApi.next();
    this.listenToLiveDevices();
    this.zoomedOutViewSocket.connect();
    timer(30000)
      .pipe(takeUntil(this.unsubscribeSocketEvents))
      .subscribe(() => {
        this.liveViewLoader = false;
      });
  }

  private listenToLiveDevices() {
    fromEvent(this.zoomedOutViewSocket, 'connect_error')
      .pipe(takeUntil(this.unsubscribeSocketEvents))
      .subscribe(() => {
        // revert to classic upgrade
        this.zoomedOutViewSocket.io.opts.transports = ['polling', 'websocket', 'webtransport'];
      });

    fromEvent<LiveTelematicsMessage>(this.zoomedOutViewSocket, 'device-update')
      .pipe(
        takeUntil(this.unsubscribeSocketEvents),
        tap(() => {
          this.liveViewLoader = false;
        }),
        filter((msg) => {
          const { messageType, assetId } = msg.value || {};
          const validMsgType = messageType === LTMessageType.Event || messageType === LTMessageType.Gps;
          return validMsgType && Boolean(assetId);
        }),
        groupBy(
          (msg) => msg.value && msg.value.assetId,
          (msg) => msg,
          (msgsByAssetId$) => msgsByAssetId$.pipe(timeoutWith(30000, EMPTY), ignoreElements())
        ),
        mergeMap((msgsByAssetId$) => msgsByAssetId$.pipe(switchMap((message) => this.liveViewService.formatLiveTelematicsMessage(message))))
      )
      .subscribe((device) => {
        const { eventType, deviceId } = device;
        const existingDeviceIndex = this.listViewAssetListDataSource.data.findIndex((d) => d?.deviceId === deviceId);
        const isNewDevice = existingDeviceIndex === -1 && eventType !== 'Vehicle-Ignition-Off';
        if (!isNewDevice) {
          this.listViewAssetListDataSource.data.splice(existingDeviceIndex, 1, device as any);
          this.listViewAssetListDataSource.data = [...this.listViewAssetListDataSource.data];
          this.updateSelectedDevice(device);
        }
      });
  }

  private updateSelectedDevice(device: LiveDevice) {
    const currentSelectedDevice = this.selectedDevice.getValue();
    const { assetId: currentSelectedAssetId = '' } = currentSelectedDevice || {};
    if (currentSelectedAssetId === device.assetId) {
      this.selectedDevice.next(device);
    }
  }

  private adjustMapWidth(windowWidth: number) {
    if (windowWidth > 1440) {
      if (this.isSideNavOpen) {
        this.mapWidth = this.viewType === 'table' ? 'calc(100vw - 620px)' : '100vw';
      } else {
        this.mapWidth = this.viewType === 'table' ? 'calc(100vw - 400px)' : '100vw';
      }
    } else {
      this.mapWidth = this.viewType === 'table' ? 'calc(100vw - 400px)' : '100vw';
    }
  }

  private getGeoLocation(params) {
    return new Promise((resolve, reject) => {
      this.tripDetailsService.getLocationData(params).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  public toggleTable() {
    this.viewType = this.viewType === 'table' ? 'map' : 'table';
    this.translateVal = this.viewType === 'table' ? 0 : 400;
    const windowWidth = window.innerWidth;
    this.gtmService.toggleEventsLiveViewTable(this.viewType === 'table' ? ToggleState.show : ToggleState.hide);
    this.adjustMapWidth(windowWidth);
  }

  public toggleMobileCard() {
    this.isMobileTableView = !this.isMobileTableView;
    this.mobileTranslateVal = this.isMobileTableView ? 0 : 70;
    this.mapWidth = '100vw';
  }

  public navigateToTripDetailsPage(device: LiveDevice, event?: MouseEvent) {
    this.liveViewService.navigateToTripDetailsPage(device, 'table');
    event?.stopPropagation();
  }

  public requestLivestreamAction(device: LiveDevice, event?: MouseEvent) {
    this.gtmService.viewLiveViewVideoAssetListTable(device.assetId);
    this.liveViewService.openLivestreamModal(device);
    event?.stopPropagation();
  }

  public selectedTags(tags) {
    this.mixpanelService.track('Select search by tags / asset', {
      Tag: tags,
    });
    this.liveViewStore.dispatch(new UpdateLiveViewTagsFilter({ tagIds: tags }));
    this.tagIds = tags;
    this.getLatestTripsAssetDetails();
  }

  public selectFilter() {
    if (this.selectedFilter.value === 'assetId') {
      this.tagIds = [];
    } else {
      this.searchAssetIdControl.reset();
      this.assetStateToFilter = [AssetState.Active, AssetState.Inactive, AssetState.Parked];
    }
  }

  public closeAssetDetails() {
    if (this.currentView === 'cluster') {
      this.recenterClusterMap = true;
      const { assetId = '' } = this.selectedDevice.getValue() || {};
      if (assetId) {
        this.drawer.close();
      }
    } else {
      if (this.drawer) {
        this.drawer.close();
      }
      if (this.mapboxLiveView) {
        setTimeout(() => {
          this.mapboxLiveView.fitBounds();
        }, 0);
      }
    }

    const currentSelectedDevice = this.selectedDevice.getValue();
    if (currentSelectedDevice?.currentLocationGeocodeDataSub) {
      currentSelectedDevice?.currentLocationGeocodeDataSub.unsubscribe();
    }
    this.selectedDevice.next(null);
  }

  public onViewTypeChange(event: string): void {
    this.liveViewStore.dispatch(new UpdateLiveViewViewType({ viewType: event }));
    this.closeAssetDetails();
    this.tagIds = [];
    this.searchAssetIdControl.reset();
    this.assetStateToFilter = [AssetState.Active, AssetState.Inactive, AssetState.Parked];
    this.assetCategories.reset({
      [AssetState.Active]: true,
      [AssetState.Inactive]: true,
      [AssetState.Parked]: true,
    });
    if (this.currentView === 'list') {
      if (!this.zoomedOutViewSocket || !this.zoomedOutViewSocket.connected) {
        this.refreshSocketConnection();
      }
      this.getLatestTripsAssetDetails();
      this.initializePaginator();
    } else {
      this.getLatestTripsAssetDetails();
    }
    if (event === 'list') {
      this.mixpanelService.track('tab-switched / live-view-page / top-navigation / List View Clicked');
    } else if (event === 'cluster') {
      this.mixpanelService.track('tab-switched / live-view-page / top-navigation / Fleet View Clicked');
    }
  }

  private getLatestTripsAssetDetails() {
    if (!this.assetStateToFilter.length) {
      this.clusteringAssetDetails = [];
      this.totalClusterAssetCount = 0;
      this.listViewAssetListDataSource.data = [];
      return;
    }
    this.clusteringAssetLoader = true;
    this.listViewAssetListDataSource.data = new Array(10).fill(undefined);
    const params: LatestTripsAssetListRequest = {
      offset: this.skip,
      limit: this.limit,
      startDate: this.startDate,
      endDate: this.endDate,
    };
    if (this.tagIds) {
      params['tagIds[]'] = this.tagIds;
    }
    if (this.searchAssetIdControl.value) {
      params['assetId'] = this.searchAssetIdControl.value;
    }
    if (this.assetStateToFilter.length < 3) {
      params['state[]'] = this.assetStateToFilter;
    }
    this.LiveViewService.getLatestTripsAssetList(params)
      .pipe(takeUntil(this.unsubscribeLatestTripApi))
      .subscribe(
        (res: LatestTripsAssetListResponse) => {
          const { rows = [], aggregateByState, totalCount = 0 } = res || {};
          this.clusteringAssetDetails = rows;
          this.aggregateByState = aggregateByState;
          this.totalClusterAssetCount = totalCount;
          this.formatStatusAgrregate();
          if (this.currentView === 'cluster') {
            if (totalCount > this.limit) {
              this.fetchAllAssetDetails();
            } else {
              this.clusteringAssetLoader = false;
            }
          } else {
            this.clusteringAssetLoader = false;
            this.mapboxLiveView.shouldFitBounds = true;
            this.listViewAssetListDataSource.data = this.liveViewService.formatLatestTrips(rows);
          }
        },
        () => {
          this.clusteringAssetDetails = [];
          this.totalClusterAssetCount = 0;
          this.listViewAssetListDataSource.data = [];
          this.aggregateByState = {
            ACTIVE: 0,
            INACTIVE: 0,
            PARKED: 0,
          };
          this.clusteringAssetLoader = false;
        }
      );
  }

  private fetchAllAssetDetails() {
    this.clusteringAssetLoader = true;
    const numberOfCallsRequired = Math.floor(this.totalClusterAssetCount / this.limit);
    const apiCalls$ = new Array(numberOfCallsRequired).fill(undefined).map((_, i) => {
      const params: LatestTripsAssetListRequest = {
        limit: this.limit,
        offset: i * this.limit + this.limit,
        startDate: this.startDate,
        endDate: this.endDate,
      };
      return this.liveViewService.getLatestTripsAssetList(params).pipe(catchError(() => of({ rows: [] })));
    });
    forkJoin(apiCalls$)
      .pipe(
        finalize(() => {
          this.clusteringAssetLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((res) => {
        res.forEach((r) => {
          this.clusteringAssetDetails = this.clusteringAssetDetails.concat(r.rows);
        });
      });
  }

  private formatStatusAgrregate() {
    const statusAggregate = Object.keys(this.aggregateByState).map((status) => {
      return {
        status: status,
        assetCount: this.aggregateByState[status],
      };
    });
    this.clusterAssetsDataSource.data = statusAggregate;
  }

  public async clusterDeviceMarkerClick(deviceDetails: TripAssetDetails | LiveDevice) {
    if ((deviceDetails as LiveDevice).partition !== undefined) {
      this.selectedDevice.next(deviceDetails as LiveDevice);
    } else {
      const activeDevice = new IntitialActiveDevice(deviceDetails as TripAssetDetails, this.dateService);
      this.selectedDevice.next(activeDevice);
    }

    if (this.currentView === 'list') {
      const { longitude = 0, latitude = 0 } = this.selectedDevice.getValue()?.gpsData || {};
      if (longitude == 0 && latitude == 0) {
        this.snackBarService.failure(this.translate.instant('liveViewComponentMarkerNotAvailbale'));
      } else {
        this.mapboxLiveView.centerMapOnMarker({
          lng: longitude,
          lat: latitude,
        });
      }
    }

    this.recenterClusterMap = false;
    this.drawer.open();
    const device = this.selectedDevice.getValue();

    if (device.currentLocationGeocodeDataSub) {
      device.currentLocationGeocodeDataSub.unsubscribe();
    }

    if (device?.state?.getValue() === AssetState.Active) {
      device.currentLocationGeocodeDataSub = timer(0, 30 * 1000).subscribe(async () => {
        const { latitude, longitude } = this.selectedDevice.getValue()?.gpsData || {};
        const params = {
          location: `${longitude},${latitude}`,
        };
        const currentGeocodedData = await this.getGeoLocation(params);
        device.currentLocationGeocodeData.next({ location: currentGeocodedData });
      });
      this.selectedDevice.next(device);
      this.LiveViewService.setDevicePreviousStatus(device.assetId, device);
    } else {
      const { longitude = 0, latitude = 0 } = device.gpsData || {};
      const params = {
        location: `${longitude},${latitude}`,
      };
      if (longitude == 0 && latitude == 0) {
        device.currentLocationGeocodeData.next({ location: '' });
      } else {
        const currentGeocodedData = await this.getGeoLocation(params);
        device.currentLocationGeocodeData.next({ location: currentGeocodedData });
      }
    }
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    const { assetId = '' } = asset || {};
    this.searchAssetIdControl.patchValue(assetId);
    this.gtmService.changeLiveviewAssetTypeFilter(assetId);
    this.getLatestTripsAssetDetails();
  }

  public durationChange(durationRange) {
    const { days, startDate, endDate } = durationRange;
    if (!(days && startDate && endDate)) {
      return;
    }
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const milliSeconds = now.getMilliseconds();

    // Setting current Hours, minutes and seconds since datepicker only sets date part.
    const from = this.dateService.toDaysStartISO(new Date(startDate?.setHours(hours, minutes, seconds, milliSeconds)));
    const to = this.dateService.toDaysEndISOPlusOne(new Date(endDate?.setHours(hours, minutes, seconds, milliSeconds)));

    const updatedDate = {
      days: days,
      displayStartDate: startDate,
      displayEndDate: endDate,
      paramStartDate: from,
      paramEndDate: to,
    };
    this.store.dispatch(new UpdateDurationFilter(updatedDate));
    this.mixpanelService.track('Change Live view Duration Filter', {
      days: days,
      startDate: startDate,
      endDate: endDate,
    });

    this.getLatestTripsAssetDetails();
  }
}
